import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import {
  parseImportFile,
  validateImportData,
  createValidationContext,
  type ParsedImportData,
  type ValidationResult,
  type ValidatedProduct,
  type ValidatedRawMaterial,
  type ValidatedModifier,
  type ValidatedCategory,
} from '@/lib/import';
import type { ImportProgressState } from '@/components/import/ImportProgress';
import type { ImportResultsData } from '@/components/import/ImportResults';

const BATCH_SIZE = 50;

export type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

export interface UseProductImportReturn {
  // State
  step: ImportStep;
  isLoading: boolean;
  error: string | null;
  parsedData: ParsedImportData | null;
  validationResult: ValidationResult | null;
  progress: ImportProgressState | null;
  results: ImportResultsData | null;

  // Actions
  parseFile: (file: File) => Promise<void>;
  startImport: () => Promise<void>;
  reset: () => void;
  setStep: (step: ImportStep) => void;
}

export function useProductImport(): UseProductImportReturn {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();

  const [step, setStep] = useState<ImportStep>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<ParsedImportData | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [progress, setProgress] = useState<ImportProgressState | null>(null);
  const [results, setResults] = useState<ImportResultsData | null>(null);

  // Fetch existing data for validation context
  const { data: existingData } = useQuery({
    queryKey: ['import-validation-context', currentLocation?.id, currentLocation?.organization_id],
    queryFn: async () => {
      if (!currentLocation) return null;

      const [products, categories, suppliers, rawMaterials, modifierGroups] = await Promise.all([
        supabase
          .from('products')
          .select('id, name, sku')
          .eq('organization_id', currentLocation.organization_id)
          .eq('is_active', true),
        supabase
          .from('categories')
          .select('id, name')
          .eq('location_id', currentLocation.id)
          .eq('is_active', true),
        supabase
          .from('suppliers')
          .select('id, name')
          .eq('organization_id', currentLocation.organization_id)
          .eq('is_active', true),
        supabase
          .from('raw_inventory_items')
          .select('id, name, sku')
          .eq('organization_id', currentLocation.organization_id)
          .eq('is_active', true),
        supabase
          .from('modifier_groups')
          .select('id, name')
          .eq('location_id', currentLocation.id),
      ]);

      return {
        products: products.data || [],
        categories: categories.data || [],
        suppliers: suppliers.data || [],
        rawMaterials: rawMaterials.data || [],
        modifierGroups: modifierGroups.data || [],
      };
    },
    enabled: !!currentLocation,
  });

  // Parse uploaded file
  const parseFile = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await parseImportFile(file);
      setParsedData(data);

      // Validate the parsed data
      if (currentLocation && existingData) {
        const context = createValidationContext(
          existingData.products,
          existingData.categories,
          existingData.suppliers,
          existingData.rawMaterials,
          existingData.modifierGroups,
          currentLocation.organization_id,
          currentLocation.id
        );

        const validated = validateImportData(data, context);
        setValidationResult(validated);
      }

      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setIsLoading(false);
    }
  }, [currentLocation, existingData]);

  // Import categories
  const importCategories = useCallback(async (
    categories: ValidatedCategory[]
  ): Promise<{ created: number; updated: number; failed: number; errors: Array<{ row: number; message: string }>; categoryMap: Map<string, string> }> => {
    const validCategories = categories.filter(c => c.isValid);
    const result = { created: 0, updated: 0, failed: 0, errors: [] as Array<{ row: number; message: string }>, categoryMap: new Map<string, string>() };

    if (!currentLocation || validCategories.length === 0) {
      // Build category map from existing
      if (existingData) {
        existingData.categories.forEach(c => {
          result.categoryMap.set(c.name.toLowerCase().trim(), c.id);
        });
      }
      return result;
    }

    // First, build map from existing categories
    existingData?.categories.forEach(c => {
      result.categoryMap.set(c.name.toLowerCase().trim(), c.id);
    });

    for (let i = 0; i < validCategories.length; i += BATCH_SIZE) {
      const batch = validCategories.slice(i, i + BATCH_SIZE);

      setProgress({
        phase: 'categories',
        current: i + batch.length,
        total: validCategories.length,
        message: `Processing categories ${i + 1} to ${i + batch.length}`,
      });

      for (const category of batch) {
        try {
          if (category.action === 'update' && category.existingId) {
            // Update existing category
            const { error } = await supabase
              .from('categories')
              .update({
                description: category.description || null,
                sort_order: category.sort_order ?? 0,
                updated_at: new Date().toISOString(),
              })
              .eq('id', category.existingId);

            if (error) throw error;
            result.updated++;
            result.categoryMap.set(category.name.toLowerCase().trim(), category.existingId);
          } else {
            // Create new category
            const { data, error } = await supabase
              .from('categories')
              .insert({
                location_id: currentLocation.id,
                name: category.name,
                description: category.description || null,
                sort_order: category.sort_order ?? 0,
                is_active: true,
              })
              .select('id')
              .single();

            if (error) throw error;
            result.created++;
            if (data) {
              result.categoryMap.set(category.name.toLowerCase().trim(), data.id);
            }
          }
        } catch (err) {
          result.failed++;
          result.errors.push({
            row: category.row,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    }

    return result;
  }, [currentLocation, existingData]);

  // Import products
  const importProducts = useCallback(async (
    products: ValidatedProduct[],
    categoryMap: Map<string, string>
  ): Promise<{ created: number; updated: number; failed: number; errors: Array<{ row: number; message: string }> }> => {
    const validProducts = products.filter(p => p.isValid);
    const result = { created: 0, updated: 0, failed: 0, errors: [] as Array<{ row: number; message: string }> };

    if (!currentLocation || validProducts.length === 0) return result;

    const { data: { user } } = await supabase.auth.getUser();
    const actingUserId = user?.id ?? null;

    for (let i = 0; i < validProducts.length; i += BATCH_SIZE) {
      const batch = validProducts.slice(i, i + BATCH_SIZE);

      setProgress({
        phase: 'products',
        current: i + batch.length,
        total: validProducts.length,
        message: `Importing products ${i + 1} to ${i + batch.length}`,
      });

      for (const product of batch) {
        try {
          // Resolve category ID
          let categoryId: string | null = null;
          if (product.category) {
            categoryId = categoryMap.get(product.category.toLowerCase().trim()) || null;
          }

          const productData = {
            name: product.name,
            sku: product.sku,
            barcode: product.barcode || null,
            price: product.price,
            cost: product.cost ?? null,
            tax_rate: product.tax_rate ?? null,
            description: product.description || null,
            category_id: categoryId,
            track_inventory: product.track_inventory ?? true,
            low_stock_threshold: product.low_stock_threshold ?? null,
            par_level: product.par_level ?? null,
            product_type: 'item' as const,
            is_active: true,
            is_available: true,
            updated_at: new Date().toISOString(),
          };

          if (product.action === 'update' && product.existingId) {
            // Update existing product
            const { error } = await supabase
              .from('products')
              .update(productData)
              .eq('id', product.existingId);

            if (error) throw error;
            result.updated++;

            // Update inventory if initial_stock is provided
            if (product.initial_stock !== undefined && product.track_inventory !== false) {
              const { data: existingInventory } = await supabase
                .from('inventory_items')
                .select('id, quantity_on_hand')
                .eq('product_id', product.existingId)
                .eq('location_id', currentLocation.id)
                .maybeSingle();

              const { data: upsertedInventory, error: inventoryUpsertError } = await supabase
                .from('inventory_items')
                .upsert({
                  product_id: product.existingId,
                  location_id: currentLocation.id,
                  quantity_on_hand: product.initial_stock,
                  quantity_foh: product.initial_stock,
                  quantity_boh: 0,
                  reorder_point: product.low_stock_threshold ?? null,
                  updated_at: new Date().toISOString(),
                }, { onConflict: 'product_id,location_id' })
                .select('id')
                .single();

              if (inventoryUpsertError) throw inventoryUpsertError;

              const quantityBefore = existingInventory?.quantity_on_hand ?? 0;
              const quantityAfter = product.initial_stock;
              const quantityChange = quantityAfter - quantityBefore;

              if (upsertedInventory?.id && quantityChange !== 0) {
                await supabase
                  .from('inventory_transactions')
                  .insert({
                    inventory_item_id: upsertedInventory.id,
                    user_id: actingUserId,
                    transaction_type: quantityBefore === 0 && quantityChange > 0 ? 'received' : 'adjusted',
                    quantity_change: quantityChange,
                    quantity_before: quantityBefore,
                    quantity_after: quantityAfter,
                    reference_type: 'import',
                    notes: 'Product import stock sync',
                  });
              }
            }
          } else {
            // Create new product
            const { data, error } = await supabase
              .from('products')
              .insert({
                ...productData,
                location_id: currentLocation.id,
                organization_id: currentLocation.organization_id,
              })
              .select('id')
              .single();

            if (error) throw error;
            result.created++;

            // Create inventory record if needed
            if (data && product.track_inventory !== false) {
              const initialQty = product.initial_stock ?? 0;
              const { data: createdInventory, error: createdInventoryError } = await supabase
                .from('inventory_items')
                .insert({
                  product_id: data.id,
                  location_id: currentLocation.id,
                  quantity_on_hand: initialQty,
                  quantity_foh: initialQty,
                  quantity_boh: 0,
                  quantity_reserved: 0,
                  reorder_point: product.low_stock_threshold ?? null,
                })
                .select('id')
                .single();

              if (createdInventoryError) throw createdInventoryError;

              if (createdInventory?.id && initialQty !== 0) {
                await supabase
                  .from('inventory_transactions')
                  .insert({
                    inventory_item_id: createdInventory.id,
                    user_id: actingUserId,
                    transaction_type: 'received',
                    quantity_change: initialQty,
                    quantity_before: 0,
                    quantity_after: initialQty,
                    reference_type: 'import',
                    notes: 'Initial stock from product import',
                  });
              }
            }
          }
        } catch (err) {
          result.failed++;
          result.errors.push({
            row: product.row,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    }

    return result;
  }, [currentLocation]);

  // Import raw materials
  const importRawMaterials = useCallback(async (
    materials: ValidatedRawMaterial[]
  ): Promise<{ created: number; updated: number; failed: number; errors: Array<{ row: number; message: string }> }> => {
    const validMaterials = materials.filter(m => m.isValid);
    const result = { created: 0, updated: 0, failed: 0, errors: [] as Array<{ row: number; message: string }> };

    if (!currentLocation || validMaterials.length === 0) return result;

    const { data: { user } } = await supabase.auth.getUser();
    const actingUserId = user?.id ?? null;

    for (let i = 0; i < validMaterials.length; i += BATCH_SIZE) {
      const batch = validMaterials.slice(i, i + BATCH_SIZE);

      setProgress({
        phase: 'rawMaterials',
        current: i + batch.length,
        total: validMaterials.length,
        message: `Importing raw materials ${i + 1} to ${i + batch.length}`,
      });

      for (const material of batch) {
        try {
          const itemData = {
            name: material.name,
            sku: material.sku || null,
            barcode: material.barcode || null,
            description: material.description || null,
            unit_of_measure: material.unit_of_measure.toLowerCase(),
            cost_per_unit: material.cost_per_unit ?? null,
            supplier_id: material.resolvedSupplierId || null,
            supplier_item_code: material.supplier_item_code || null,
            category: material.category?.toLowerCase() || 'other',
            is_active: true,
            updated_at: new Date().toISOString(),
          };

          if (material.action === 'update' && material.existingId) {
            // Update existing raw material
            const { error } = await supabase
              .from('raw_inventory_items')
              .update(itemData)
              .eq('id', material.existingId);

            if (error) throw error;
            result.updated++;

            // Update stock if provided
            if (material.initial_stock !== undefined || material.reorder_point !== undefined) {
              const { data: existingStock } = await supabase
                .from('raw_inventory_stock')
                .select('quantity_on_hand')
                .eq('inventory_item_id', material.existingId)
                .eq('location_id', currentLocation.id)
                .maybeSingle();

              const nextQuantityOnHand = material.initial_stock !== undefined
                ? material.initial_stock
                : (existingStock?.quantity_on_hand ?? 0);

              const { error: stockUpsertError } = await supabase
                .from('raw_inventory_stock')
                .upsert({
                  inventory_item_id: material.existingId,
                  location_id: currentLocation.id,
                  quantity_on_hand: nextQuantityOnHand,
                  reorder_point: material.reorder_point ?? null,
                  reorder_quantity: material.reorder_quantity ?? null,
                  updated_at: new Date().toISOString(),
                }, { onConflict: 'inventory_item_id,location_id' });

              if (stockUpsertError) throw stockUpsertError;

              if (material.initial_stock !== undefined) {
                const quantityBefore = existingStock?.quantity_on_hand ?? 0;
                const quantityAfter = material.initial_stock;
                const quantityChange = quantityAfter - quantityBefore;

                if (quantityChange !== 0) {
                  await supabase
                    .from('raw_inventory_transactions')
                    .insert({
                      inventory_item_id: material.existingId,
                      location_id: currentLocation.id,
                      transaction_type: quantityBefore === 0 && quantityChange > 0 ? 'received' : 'count',
                      quantity_change: quantityChange,
                      quantity_before: quantityBefore,
                      quantity_after: quantityAfter,
                      reference_type: 'import',
                      notes: 'Raw material import stock sync',
                      performed_by: actingUserId,
                    });
                }
              }
            }
          } else {
            // Create new raw material
            const { data, error } = await supabase
              .from('raw_inventory_items')
              .insert({
                ...itemData,
                organization_id: currentLocation.organization_id,
              })
              .select('id')
              .single();

            if (error) throw error;
            result.created++;

            // Create stock record
            if (data) {
              const initialQty = material.initial_stock ?? 0;
              await supabase
                .from('raw_inventory_stock')
                .insert({
                  inventory_item_id: data.id,
                  location_id: currentLocation.id,
                  quantity_on_hand: initialQty,
                  quantity_reserved: 0,
                  reorder_point: material.reorder_point ?? null,
                  reorder_quantity: material.reorder_quantity ?? null,
                });

              if (initialQty !== 0) {
                await supabase
                  .from('raw_inventory_transactions')
                  .insert({
                    inventory_item_id: data.id,
                    location_id: currentLocation.id,
                    transaction_type: 'received',
                    quantity_change: initialQty,
                    quantity_before: 0,
                    quantity_after: initialQty,
                    reference_type: 'import',
                    notes: 'Initial stock from raw material import',
                    performed_by: actingUserId,
                  });
              }
            }
          }
        } catch (err) {
          result.failed++;
          result.errors.push({
            row: material.row,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    }

    return result;
  }, [currentLocation]);

  // Import modifiers
  const importModifiers = useCallback(async (
    modifiers: ValidatedModifier[]
  ): Promise<{ groupsCreated: number; optionsCreated: number; failed: number; errors: Array<{ row: number; message: string }> }> => {
    const validModifiers = modifiers.filter(m => m.isValid);
    const result = { groupsCreated: 0, optionsCreated: 0, failed: 0, errors: [] as Array<{ row: number; message: string }> };

    if (!currentLocation || validModifiers.length === 0) return result;

    // Group modifiers by group name
    const groupMap = new Map<string, ValidatedModifier[]>();
    for (const modifier of validModifiers) {
      const key = modifier.group_name.toLowerCase().trim();
      const existing = groupMap.get(key) || [];
      existing.push(modifier);
      groupMap.set(key, existing);
    }

    // Get existing modifier groups
    const { data: existingGroups } = await supabase
      .from('modifier_groups')
      .select('id, name')
      .eq('location_id', currentLocation.id);

    const existingGroupMap = new Map<string, string>();
    existingGroups?.forEach(g => {
      existingGroupMap.set(g.name.toLowerCase().trim(), g.id);
    });

    let processed = 0;
    const totalGroups = groupMap.size;

    for (const [groupKey, options] of groupMap) {
      processed++;
      setProgress({
        phase: 'modifiers',
        current: processed,
        total: totalGroups,
        message: `Processing modifier group ${processed} of ${totalGroups}`,
      });

      try {
        // Get or create modifier group
        let groupId = existingGroupMap.get(groupKey);
        const firstOption = options[0];

        if (!groupId) {
          // Create new group
          const { data: newGroup, error } = await supabase
            .from('modifier_groups')
            .insert({
              location_id: currentLocation.id,
              name: firstOption.group_name,
              min_selections: firstOption.min_selections ?? 0,
              max_selections: firstOption.max_selections ?? null,
              is_required: firstOption.is_required ?? false,
            })
            .select('id')
            .single();

          if (error) throw error;
          groupId = newGroup?.id;
          result.groupsCreated++;
        }

        if (!groupId) continue;

        // Create options for this group
        for (const option of options) {
          try {
            const { error } = await supabase
              .from('modifier_options')
              .insert({
                modifier_group_id: groupId,
                name: option.option_name,
                price_adjustment: option.price_adjustment ?? 0,
                is_default: option.is_default ?? false,
                is_active: true,
                sort_order: option.sort_order ?? 0,
              });

            if (error) throw error;
            result.optionsCreated++;
          } catch (err) {
            result.failed++;
            result.errors.push({
              row: option.row,
              message: err instanceof Error ? err.message : 'Unknown error',
            });
          }
        }
      } catch (err) {
        result.failed += options.length;
        options.forEach(o => {
          result.errors.push({
            row: o.row,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        });
      }
    }

    return result;
  }, [currentLocation]);

  // Start the import process
  const startImport = useCallback(async () => {
    if (!validationResult || !currentLocation) return;

    setStep('importing');
    setError(null);

    const importResults: ImportResultsData = {
      products: { created: 0, updated: 0, failed: 0, errors: [] },
      rawMaterials: { created: 0, updated: 0, failed: 0, errors: [] },
      modifiers: { groupsCreated: 0, optionsCreated: 0, failed: 0, errors: [] },
      categories: { created: 0, updated: 0, failed: 0, errors: [] },
    };

    try {
      // 1. Import categories first (products depend on them)
      const categoryResults = await importCategories(validationResult.categories);
      importResults.categories = {
        created: categoryResults.created,
        updated: categoryResults.updated,
        failed: categoryResults.failed,
        errors: categoryResults.errors,
      };

      // Also create categories referenced in products
      const productCategories = new Set<string>();
      validationResult.products.forEach(p => {
        if (p.category && p.isValid) {
          productCategories.add(p.category.toLowerCase().trim());
        }
      });

      for (const catName of productCategories) {
        if (!categoryResults.categoryMap.has(catName)) {
          // Find the original category name (with proper casing)
          const product = validationResult.products.find(
            p => p.category?.toLowerCase().trim() === catName
          );
          if (product?.category) {
            try {
              const { data } = await supabase
                .from('categories')
                .insert({
                  location_id: currentLocation.id,
                  name: product.category,
                  is_active: true,
                  sort_order: 0,
                })
                .select('id')
                .single();

              if (data) {
                categoryResults.categoryMap.set(catName, data.id);
                importResults.categories.created++;
              }
            } catch {
              // Category might already exist, try to fetch it
              const { data } = await supabase
                .from('categories')
                .select('id')
                .eq('location_id', currentLocation.id)
                .ilike('name', product.category)
                .single();

              if (data) {
                categoryResults.categoryMap.set(catName, data.id);
              }
            }
          }
        }
      }

      // 2. Import products
      const productResults = await importProducts(validationResult.products, categoryResults.categoryMap);
      importResults.products = productResults;

      // 3. Import raw materials
      const rawMaterialResults = await importRawMaterials(validationResult.rawMaterials);
      importResults.rawMaterials = rawMaterialResults;

      // 4. Import modifiers
      const modifierResults = await importModifiers(validationResult.modifiers);
      importResults.modifiers = modifierResults;

      setResults(importResults);
      setStep('results');

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['modifier-groups'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setResults(importResults);
      setStep('results');
    }
  }, [validationResult, currentLocation, importCategories, importProducts, importRawMaterials, importModifiers, queryClient]);

  // Reset state
  const reset = useCallback(() => {
    setStep('upload');
    setIsLoading(false);
    setError(null);
    setParsedData(null);
    setValidationResult(null);
    setProgress(null);
    setResults(null);
  }, []);

  return {
    step,
    isLoading,
    error,
    parsedData,
    validationResult,
    progress,
    results,
    parseFile,
    startImport,
    reset,
    setStep,
  };
}
